import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

export type AlertVariant = "info" | "success" | "warning" | "error";

type AlertModalProps = {
	open: boolean;
	onClose: () => void;
	title: string;
	message: string;
	variant?: AlertVariant;
	confirmText?: string;
};

const variantStyles: Record<AlertVariant, { icon: typeof Info; badgeClasses: string; titleClasses: string; messageClasses: string }> = {
	info: {
		icon: Info,
		badgeClasses: "bg-primary/10 text-primary",
		titleClasses: "text-slate-900",
		messageClasses: "text-slate-500",
	},
	success: {
		icon: CheckCircle2,
		badgeClasses: "bg-primary/10 text-primary",
		titleClasses: "text-slate-900",
		messageClasses: "text-slate-500",
	},
	warning: {
		icon: AlertTriangle,
		badgeClasses: "bg-amber-100 text-amber-600",
		titleClasses: "text-amber-700",
		messageClasses: "text-amber-600",
	},
	error: {
		icon: AlertCircle,
		badgeClasses: "bg-rose-100 text-rose-500",
		titleClasses: "text-rose-600",
		messageClasses: "text-rose-500",
	},
};

export function AlertModal({
	open,
	onClose,
	title,
	message,
	variant = "info",
	confirmText = "Entendido",
}: AlertModalProps) {
	if (!open) return null;

	const { icon: Icon, badgeClasses, titleClasses, messageClasses } = variantStyles[variant];

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6' onClick={onClose}>
			<div
				className='relative w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-2xl bg-white px-5 sm:px-6 py-5 soft-border card-shadow'
				onClick={(event) => event.stopPropagation()}
			>
				<button
					type='button'
					onClick={onClose}
					aria-label='Cerrar'
					className='absolute right-4 top-4 rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 cursor-pointer'
				>
					<X className='h-4 w-4' />
				</button>

				<div className='flex items-start gap-3'>
					<span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${badgeClasses}`}>
						<Icon className='h-5 w-5' />
					</span>
					<div>
						<h2 className={`text-base font-semibold ${titleClasses}`}>{title}</h2>
						<p className={`mt-1 text-sm ${messageClasses}`}>{message}</p>
					</div>
				</div>

				<div className='mt-6 flex justify-end'>
					<button
						type='button'
						onClick={onClose}
						className='inline-flex w-full justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover cursor-pointer'
					>
						{confirmText}
					</button>
				</div>
			</div>
		</div>
	);
}
